import { css } from '@emotion/react'
import { FieldValidator } from 'final-form'
import { FC, useMemo } from 'react'
import { useField } from 'react-final-form'

import ConfirmText from '~/form/components/atoms/ConfirmText'
import FieldError from '~/form/components/atoms/FieldError'
import FieldLabel, { FieldLabelProps } from '~/form/components/atoms/FieldLabel'
import FieldWrapper from '~/form/components/atoms/FieldWrapper'
import HelperText from '~/form/components/atoms/HelperText'
import InputText from '~/form/components/molecules/InputText'
import useValidationErrorClassName from '~/form/hooks/useValidationErrorClassName'

export type Props = {
  name: string
  isConfirm: boolean
  helperText?: string
  placeholder?: string
  type?: string
  ariaLabel?: string
  parse?: (value: string, name: string) => string
  validate?: FieldValidator<string>
} & FieldLabelProps

const TextField: FC<Props> = ({
  name,
  label,
  required,
  htmlFor,
  isConfirm,
  placeholder,
  helperText,
  type = 'text',
  ariaLabel,
  parse,
  validate,
}) => {
  const { meta, input } = useField(name)

  const isShowError = useMemo(
    () => meta.error && meta.touched,
    [meta.error, meta.touched]
  )

  const isMultipleConfirm = useMemo(
    () => isConfirm && input.value === '',
    [input.value, isConfirm]
  )

  const wrapperClassName = useValidationErrorClassName(isShowError)

  return (
    <div className={wrapperClassName} css={isMultipleConfirm && hiddenStyle}>
      <FieldWrapper>
        <FieldLabel
          label={label}
          required={required}
          htmlFor={htmlFor}
          isShowError={isShowError}
        />
        <div css={[fieldDetailGroupStyle()]}>
          {isConfirm ? (
            <ConfirmText value={input.value} />
          ) : (
            <>
              <InputText
                htmlFor={htmlFor}
                isShowError={isShowError}
                name={name}
                type={type}
                placeholder={placeholder}
                ariaLabel={ariaLabel}
                parse={parse}
                validate={validate}
              />
              <FieldError isShowError={isShowError} error={meta.error} />
              <HelperText text={helperText} />
            </>
          )}
        </div>
      </FieldWrapper>
    </div>
  )
}

export default TextField

const fieldDetailGroupStyle = () => css`
  display: grid;
  row-gap: 5px;

  @media (max-width: 767px) {
    row-gap: 7px;
  }
`

const hiddenStyle = css`
  display: none;
`
